<template>
  <div class="chart-arts-overview searchable-table p-4" :class="{ 'chart-arts-overview--social-mode': socialMode }">
    <h4>{{ chartRoute.title }}</h4>
    <p>The reporting team tracked relics that museums received directly through Douglas Latchford, as well as Khmer relics acquired from the Nancy Wiener and Doris Wiener galleries, or the London auction house Spink & Son, which prosecutors say sold looted antiquities.</p>
    <b-input-group class="mb-4" size="sm">
      <b-input-group-prepend is-text>
        <fa icon="filter" />
      </b-input-group-prepend>
      <b-form-input v-model="filter" type="search" placeholder="Filter museums by name or location"/>
    </b-input-group>
    <b-table
      :fields="fields"
      :dark="socialMode"
      :items="items"
      class="m-0"
      responsive
      small
      table-class="chart-arts-overview__table small"
      tbody-tr-class="chart-arts-overview__table__row"
      thead-tr-class="chart-arts-overview__table__header">
      <template #cell(museum)="{ item: { museum } }">
        <span v-html="highlight(museum)"></span>
      </template>
      <template #cell(location)="{ item: { location } }">
        <span v-html="highlight(location)"></span>
      </template>
      <template #cell(piecesLinkedToLatchford)="{ item: { piecesLinkedToLatchford } }">
        <template v-if="piecesLinkedToLatchford > 0">
          {{ piecesLinkedToLatchford}}
        </template>
        <template v-else>
          <span class="text-muted">-</span>
        </template>
      </template>
      <template #cell(piecesLinkedToAssociates)="{ item: { piecesLinkedToAssociates } }">
        <template v-if="piecesLinkedToAssociates > 0">
          {{ piecesLinkedToAssociates}}
        </template>
        <template v-else>
          <span class="text-muted">-</span>
        </template>
      </template>
    </b-table>
    <p class="text-muted mt-2 small text-right">Source: ICIJ, The Washington Post and partners</p>
    <p class="mt-2 font-italic">Note: Pieces linked to associates include only Khmer relics. Reporters identified 23 additional Khmer pieces in 11 museums that have no provenance beyond 1970. There may well be more. For more about this survey and the museums’ responses, <a href="https://www.icij.org/investigations/pandora-papers/how-we-tracked-ancient-cambodian-antiquities-leading-museums/" target="_blank">see our full methodology</a>.</p>
  </div>
</template>

<script>
import * as d3 from 'd3'
import Fuse from 'fuse.js'
import { camelCase, escapeRegExp, mapKeys } from 'lodash'
import chart from '../mixins/chart'

export default {
  mixins: [chart],
  name: 'ChartArtsOverview',
  filters: {
    formatNumber: d3.format(',')
  },
  data () {
    return {
      filter: ''
    }
  },
  methods: {
    highlight (text) {
      if (this.filter && text) {
        const regex = new RegExp('(' + escapeRegExp(this.filter) + ')', 'gi')
        return text.replace(regex, '<mark class="p-0">$1</mark>')
      }
      return text
    }
  },
  computed: {
    tsv () {
      return this.$options.tsv.map(row => {
        return mapKeys(row, (_, k) => camelCase(k))
      })
    },
    fuse () {
      return new Fuse(this.tsv, {
        includeScore: false,
        includeMatches: false,
        shouldSort: false,
        threshold: 0.1,
        keys: ['museum', 'location']
      })
    },
    tsvFiltered () {
      if (this.filter) {
        return this.fuse.search(this.filter).map(match => match.item)
      }
      return this.tsv
    },
    items () {
      return this.tsvFiltered
    },
    fields () {
      return [
        {
          key: 'museum',
          sortable: true,
          label: 'Museum',
          thClass: '--museum',
          tdClass: '--museum'
        },
        {
          key: 'location',
          sortable: true,
          label: 'Location',
          thClass: '--location',
          tdClass: '--location'
        },
        {
          key: 'piecesLinkedToLatchford',
          sortable: true,
          label: 'Pieces linked to Latchford',
          thClass: '--pieces-linked-to-latchford',
          tdClass: '--pieces-linked-to-latchford'
        },
        {
          key: 'piecesLinkedToAssociates',
          sortable: true,
          label: 'Pieces linked to associates',
          thClass: '--pieces-linked-to-associates',
          tdClass: '--pieces-linked-to-associates'
        }
      ]
    }
  }
}
</script>

<style lang="scss">
@import '../variables.scss';

.chart-arts-overview {

  &__table {
    border: 1px solid $table-border-color;

    .chart-arts-overview--social-mode & {
      border: 0;
    }

    &__header {
      background: $primary;
      color: white;

      table.b-table.table thead > & > th {
        padding-right: $spacer !important;
        vertical-align: middle;
        text-align: center;

        &.--pieces-linked-to-latchford,
        &.--pieces-linked-to-associates {
          min-width: 90px;
        }
      }
    }

    &__row {
      white-space: nowrap;

      td.--museum {
        font-weight: bold;
      }

      td.--pieces-linked-to-latchford,
      td.--pieces-linked-to-associates {
        color: $success;
        text-align: center;
        min-width: 90px;
      }

    }
  }
}
</style>

<tsv>
Museum	Location	Pieces linked to Latchford	Pieces linked to associates
Metropolitan Museum of Art	New York, N.Y.	12	7
Denver Art Museum	Denver, Colo.	6	0
British Museum	London, United Kingdom	5	0
Cleveland Museum of Art	Cleveland, Ohio	3	2
Museum of Fine Arts	Boston, Mass.	0	2
Asian Art Museum	San Francisco, Calif.	0	2
Brooklyn Museum	New York, N.Y.	0	1
National Gallery of Australia	Canberra, Australia	1	0
Walters Art Museum	Baltimore, Md.	0	1
Los Angeles County Museum of Art	Los Angeles, Calif.	0	1
</tsv>
